import { useEffect } from 'react'
import { WEB_ASSETS, notificationCountIconKey } from '@tenfour/contracts'
import { TenFourText } from '@/components/TenFourText'
import { tutorialById, tutorialForPath, tutorialLength } from '@/components/tutorials/registry'
import type { TutorialDef } from '@/components/tutorials/types'
import { TutorialPlayer, TutorialStill } from '@/components/tutorials/TutorialPlayer'
import { useTutorialProgress } from '@/components/tutorials/useTutorialProgress'
import styles from './help.module.css'

export { tutorialById as findTutorial }

/** 24px clay count disc — the numbered-step mark in the tutorial view. */
function ClayStepNumber({ n }: { n: number }) {
  const asset = WEB_ASSETS.icons.punctuation.counts[notificationCountIconKey(n)]
  return (
    <img
      className={styles.stepNumber}
      src={asset.oneX}
      srcSet={`${asset.oneX} 1x, ${asset.twoX} 2x`}
      width={24}
      height={24}
      alt=""
      aria-hidden="true"
      decoding="async"
      draggable={false}
    />
  )
}

function PlayGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M8 5.5v13a1 1 0 0 0 1.52.85l10.4-6.5a1 1 0 0 0 0-1.7L9.52 4.65A1 1 0 0 0 8 5.5Z" fill="currentColor" />
    </svg>
  )
}

/**
 * Help → This page: the current page's explanation video as one card — a
 * still of the film with a navy play disc and its length, then the title, a
 * two-line summary, and "New" until it has been watched. Hidden on pages
 * without a tutorial.
 */
export function ThisPageCard({ pathname, onOpen }: { pathname: string; onOpen: (tutorialId: string) => void }) {
  const tutorial = tutorialForPath(pathname)
  const progress = useTutorialProgress()
  if (!tutorial) return null
  const length = tutorialLength(tutorial)
  const unwatched = progress.isUnwatched(tutorial)

  return (
    <section aria-labelledby="help-this-page-title" className={styles.section}>
      <h3 id="help-this-page-title" className={styles.sectionLabel}>
        This page
      </h3>
      <button
        type="button"
        className={styles.pageCard}
        data-help-tutorial={tutorial.id}
        onClick={() => onOpen(tutorial.id)}
        onPointerEnter={() => void tutorial.preload?.()}
        onFocus={() => void tutorial.preload?.()}
        aria-label={`Watch the ${tutorial.title} explanation video · ${length}${unwatched ? ' · New' : ''}`}
      >
        <span className={styles.pageCardPoster}>
          <TutorialStill tutorial={tutorial} className={styles.pageCardStill} />
          <span className={styles.playDisc} aria-hidden="true">
            <PlayGlyph />
          </span>
          <span className={styles.lengthChip} aria-hidden="true">
            {length}
          </span>
        </span>
        <span className={styles.pageCardText} aria-hidden="true">
          <span className={styles.pageCardTitleRow}>
            <span className={styles.pageCardTitle}>{tutorial.title}</span>
            {unwatched && <span className={styles.newPill}>New</span>}
          </span>
          <span className={styles.pageCardSummary}>
            <TenFourText>{tutorial.explanation.summary}</TenFourText>
          </span>
        </span>
      </button>
    </section>
  )
}

/**
 * Help's tutorial view: the full player (controls, chapters, scrubber;
 * reduced motion shows the poster and waits for Play), then what the page is
 * for and its numbered steps. Opening it counts as watched, which retires the
 * peek tab and ticks Get set up's "Review a decision".
 */
export function TutorialView({ tutorial, onTakeTour }: { tutorial: TutorialDef; onTakeTour?: () => void }) {
  const { markSeen } = useTutorialProgress()

  useEffect(() => {
    markSeen(tutorial)
    // Once per tutorial opened; markSeen's identity is irrelevant here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorial.id, tutorial.version])

  return (
    <article className={styles.tutorial} aria-labelledby="help-tutorial-title">
      <h3 id="help-tutorial-title" className={styles.tutorialTitle}>
        {tutorial.title}
        <span className={styles.tutorialLength}>{tutorialLength(tutorial)}</span>
      </h3>
      <div className={styles.tutorialPlayer}>
        <TutorialPlayer tutorial={tutorial} variant="full" autoPlay />
      </div>

      <section aria-labelledby="help-tutorial-about" className={styles.tutorialAbout}>
        <h4 id="help-tutorial-about" className={styles.sectionLabel}>
          What this page is for
        </h4>
        <p className={styles.tutorialSummary}>
          <TenFourText>{tutorial.explanation.summary}</TenFourText>
        </p>
      </section>

      <ol className={styles.tutorialSteps} aria-label="How it works">
        {tutorial.explanation.steps.map((step, index) => (
          <li key={step.title} className={styles.tutorialStep}>
            <ClayStepNumber n={index + 1} />
            <div className={styles.tutorialStepText}>
              <span className={styles.tutorialStepTitle}>{step.title}</span>
              <span className={styles.tutorialStepBody}>
                <TenFourText>{step.body}</TenFourText>
              </span>
            </div>
          </li>
        ))}
      </ol>

      {onTakeTour && (
        <div className={styles.tutorialTour}>
          <button type="button" className={styles.tourBtn} onClick={onTakeTour}>
            Take the guided tour
          </button>
          <span className={styles.tourHint}>Four quick stops on Home, right on the page.</span>
        </div>
      )}
    </article>
  )
}
